import { useState } from 'react';
import Modal from '../Modal/Modal';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import { FormikErrors, useFormik } from 'formik';
import MiniLoader from '../MiniLoader/MiniLoader';
import Checkbox from '../Checkbox/Checkbox';
import TextArea from '../TextArea/TextArea';
import { Option } from './ErrorFeedbackModal.helpers';
interface Props {
  isOpened: boolean;
  closeModal: () => void;
  title: string;
  onSubmit: (
    values: ErrorFeedbackModalValues,
    setIsLoding: (v: boolean) => void
  ) => void;
  options: Option[];
  defaultOption?: string;
  description: string;
}

export interface ErrorFeedbackModalValues {
  option: string;
  comment: string;
}

export interface ErrorFeedbackModalData {
  error: string;
  onSent: () => void;
}

export const ErrorFeedbackModal = ({
  isOpened,
  closeModal,
  title,
  onSubmit,
  options,
  defaultOption = 'other',
  description,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik<ErrorFeedbackModalValues>({
    validateOnBlur: true,
    initialValues: {
      option: defaultOption,
      comment: '',
    },
    validate: (values) => {
      const errors: FormikErrors<ErrorFeedbackModalValues> = {};
      if (!values.option) {
        errors.option = t('Required')!;
      }

      return errors;
    },
    onSubmit: (values) => {
      onSubmit(values, setIsLoading);
    },
  });

  return (
    <Modal
      isOpened={isOpened}
      close={closeModal}
      title={t(title)}
      isLoading={isLoading}
      name='error-feedback'
    >
      <div className='mx-4 px-4 pb-6 pt-2'>
        <div className='text-h4 font-extrabold'>
          {t("What wasn't clear to you?")}
        </div>
        <div className='mb-6'>{t(description)}</div>
        <form onSubmit={formik.handleSubmit}>
          <div className='relative mb-2 space-y-5 pb-6 font-semibold'>
            {options.map((option) => (
              <Checkbox
                type='radio'
                key={option.id}
                name={`option-${option.id}`}
                onChange={() => {
                  formik.setFieldValue('option', option.id, true);
                  setTimeout(() => {
                    formik.setFieldTouched('option', true);
                  }, 0);
                }}
                checked={formik.values.option === option.id}
                labelId='option_area'
                className='RegisterForm__checkbox'
                isError={!!formik.errors.option}
              >
                {t(option.label)}
              </Checkbox>
            ))}
            {formik.touched.option && formik.errors.option && (
              <div
                className='RegisterForm__error absolute'
                style={{
                  bottom: 0,
                  left: 0,
                }}
                data-testid='consent-error'
              >
                {t(formik.errors.option)}
              </div>
            )}
          </div>
          <TextArea
            labelClassName='font-semibold'
            placeholder={'Additional comments'}
            value={formik.values.comment}
            onChange={formik.handleChange}
            name='comment'
            label='Any additional comments? (optional)'
          />
          <div className='mt-2 flex flex-wrap gap-x-2'>
            <div>
              <Button
                className='flex items-center'
                type='submit'
                disabled={isLoading}
                data-testid='error-feedback-submit-btn'
              >
                {isLoading && <MiniLoader className='mr-2' color='gray' />}
                <span>{t('Submit')}</span>
              </Button>
            </div>
            <div>
              <Button
                type='button'
                styling='outline'
                onClick={closeModal}
                disabled={isLoading}
                data-testid='error-feedback-cancel-btn'
              >
                {t('Cancel')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};
